import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import * as XLSX from "xlsx";
import { Card, Form, Button } from "react-bootstrap";
import GradeAcademyProcess from "./GradeAcademyProcess";
import { EntitlementsProvider, useEntitlements } from './context/EntitlementsContext';
import Feature from './components/Feature';
import { useLimit } from './hooks/useLimit';
import UpgradeBanner from './components/UpgradeBanner';
import DevBypassFeature from "./DevBypassFeature";
import { dotStream } from 'ldrs'

dotStream.register()
const FileUpload = (props) => {
  const [lineData, setLineData] = useState([]);
  const [enrollmentData, setEnrollmentData] = useState([]);
  const [filterData, setFilterData] = useState([]);

  const [fileUploadVisible, setFileUploadVisible] = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  const [step, setStep] = useState(1); // 1: Choose, 2: Upload, (then handoff)
  const [loadingDemo, setLoadingDemo] = useState(false);

  // File refs (for traditional input)
  const gradeRef = useRef(null);
  const enrollRef = useRef(null);
  const filterRef = useRef(null);

  // File state (for drag&drop)
  const [gradeFile, setGradeFile] = useState(null);
  const [enrollFile, setEnrollFile] = useState(null);
  const [filterFile, setFilterFile] = useState(null);

  // ---------- Banned-name check ----------
  async function checkNamesBeforeProcessing(input, bannedNamesUrl = "/names.csv", idColumn = "ID") {
    // 1) Load banned names from names.csv
    const bannedText = await fetch(bannedNamesUrl).then(r => {
      if (!r.ok) throw new Error("Failed to load names.csv");
      return r.text();
    });
    const bannedNames = new Set(
      bannedText
        .trim()
        .split(/\r?\n/)
        .slice(1) // skip header "name"
        .map(line => line.split(",")[0]?.trim().toLowerCase())
        .filter(Boolean)
    );

    // 2) Normalize input to a JSON array of rows (objects) using XLSX
    const rows = await (async () => {
      // a) File/Blob
      if (input instanceof File || (typeof Blob !== "undefined" && input instanceof Blob)) {
        const buf = await input.arrayBuffer();
        const wb  = XLSX.read(buf, { type: "array" });
        const sheet = wb.SheetNames[0];
        return XLSX.utils.sheet_to_json(wb.Sheets[sheet]); // [{ID: "...", ...}, ...]
      }

      // b) Raw CSV/Text
      if (typeof input === "string" && input.includes("\n")) {
        const wb  = XLSX.read(input, { type: "string" });
        const sheet = wb.SheetNames[0];
        return XLSX.utils.sheet_to_json(wb.Sheets[sheet]);
      }

      // c) URL → fetch text then parse
      if (typeof input === "string") {
        const txt = await fetch(input).then(r => {
          if (!r.ok) throw new Error(`Failed to load dataset: ${r.status}`);
          return r.text();
        });
        const wb  = XLSX.read(txt, { type: "string" });
        const sheet = wb.SheetNames[0];
        return XLSX.utils.sheet_to_json(wb.Sheets[sheet]);
      }

      throw new Error("Unsupported input type for checkNamesBeforeProcessing");
    })();

    // 3) Locate the ID column (case-insensitive), fallback to first column
    const idKey = (() => {
      if (!rows.length) return null;
      const keys = Object.keys(rows[0] || {});
      const exact = keys.find(k => k.trim().toLowerCase() === idColumn.toLowerCase());
      return exact || keys[0] || null;
    })();

    if (!idKey) {
      // No columns → nothing to check
      return true;
    }

    // 4) Scan IDs against banned names (case-insensitive)
    for (const r of rows) {
      const val = String(r[idKey] ?? "").trim();
      if (!val) continue;
      if (bannedNames.has(val.toLowerCase())) {
        const confirmed = window.confirm(
          `❌ Match found: "${val}" is in the banned data list to prevent FERPA violations.\n\n` +
          `Contact your IT or research department to assign numeric values or hash values for each student name before processing.\n\n` +
          `If you believe this is in error, click Cancel to report this issue to the development team.`
        );

        if (confirmed) {
          // User clicked OK → reload the page
          window.location.reload();
        } else {
          // User clicked Cancel → open contact page in a new tab/window
          window.open("https://grade.academy/contact", "_blank", "noopener,noreferrer");
        }
        return false;
      }

    }

    // 5) No matches
    return true;
  }

// Example usage
  // ---------- Helpers ----------
    // ---------- Helpers (updated) ----------
  const readExcel = async (file, setter) => {
    // ✅ Await the check BEFORE reading  
    const ok = await checkNamesBeforeProcessing(file, "/names.csv", "ID");
    if (!ok) window.location.reload();

    const p = new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.readAsArrayBuffer(file);
      fr.onload = e => {
        const wb = XLSX.read(e.target.result, { type: "buffer" });
        const sheet = wb.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(wb.Sheets[sheet]);
        resolve(data);
      };
      fr.onerror = err => reject(err);
    });

    p.then(setter).catch(console.error);
  };

  function parseCsvString(csvString, setter) {
    const wb = XLSX.read(csvString, { type: "string" });
    const sheet = wb.SheetNames[0];
    const json = XLSX.utils.sheet_to_json(wb.Sheets[sheet]);
    setter(json);
  }

  function parseCsvString(csvString, setter) {
    // Treat fetch() text as string input
    const wb = XLSX.read(csvString, { type: "string" });
    const sheet = wb.SheetNames[0];
    const json = XLSX.utils.sheet_to_json(wb.Sheets[sheet]);
    setter(json);
  }

  // ---------- Demo loader (optional check for demo files) ----------
  const handleDemo = async () => {
    try {
      setLoadingDemo(true);

      const gradesRes = await fetch("/350_grades.csv");
      if (!gradesRes.ok) throw new Error(`Grades fetch failed: ${gradesRes.status}`);
      const gradesText = await gradesRes.text();

      // Optional: block demo data if it contains banned names
      const okDemo = await checkNamesBeforeProcessing(gradesText, "/names.csv", "ID");
      if (!okDemo) return;

      parseCsvString(gradesText, setLineData);

      const enrollRes = await fetch("/350_enrollment.csv");
      if (!enrollRes.ok) throw new Error(`Enrollment fetch failed: ${enrollRes.status}`);
      const enrollText = await enrollRes.text();
      parseCsvString(enrollText, setEnrollmentData);

      setDemoMode(true);
      setFileUploadVisible(false);
    } catch (err) {
      console.error("Demo load failed:", err);
    } finally {
      setLoadingDemo(false);
    }
  };


 // ---------- Processing flow (await readExcel) ----------
  const handleProcessFiles = async () => {
    const selectedGrade  = gradeRef.current?.files?.[0]  || gradeFile;
    const selectedEnroll = enrollRef.current?.files?.[0] || enrollFile;
    const selectedFilter = filterRef.current?.files?.[0] || filterFile;

    if (!selectedGrade) {
      alert("Please upload a grade data file (CSV/XLSX) to continue.");
      return;
    }

    // ✅ Ensure checks run before loading into state
    if (selectedGrade)  await readExcel(selectedGrade, setLineData);
    if (selectedEnroll) await readExcel(selectedEnroll, setEnrollmentData);

    if (selectedFilter) {
      const text = await selectedFilter.text();
      const ids = text.split(/[\s,]+/).filter(Boolean).map(x => ({ id: x }));
      setFilterData(ids);
    }

    setFileUploadVisible(false);
  };


  // ---------- Drag & Drop ----------
  const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
  const pickFile = (accept, cb) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => cb(input.files[0]);
    input.click();
  };

  const Dropzone = ({ label, required, file, onFile, accept, helper, sampleHref }) => (
    <><div
      className={`dropzone ${required ? "dz-parent-required" : ""}`}
      onDragEnter={prevent}
      onDragOver={prevent}
      onDrop={(e) => {
        prevent(e);
        const f = e.dataTransfer?.files?.[0];
        if (f) onFile(f);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") pickFile(accept, onFile); }}
      onClick={() => pickFile(accept, onFile)}
      aria-label={`${label}${required ? " (required)" : ""}`}
    >
      <div className="dz-top">
        <div className="dz-title">
          {label} {required && <span className="req">(Required)</span>}
        </div>
        {file ? (
          <div className="dz-file">{file.name}</div>
        ) : (
          <div className="dz-cta">Click or drop a file here</div>
        )}
      </div>
    </div>
     {helper && (
        <div className="dz-help">
          {helper}{" "}
          {sampleHref && (
            <>
            <br/>
              <a href={sampleHref} target="_blank" rel="noreferrer">
                Download sample (provide to technology support for data export)
              </a>
              .
            </>
          )}
        </div>
      )}</>
  );
  const ent = useEntitlements();
  const { allowed, limit, plan, loading } = useLimit('maxStudents');
  {/* useEffect(() => {
    // Ask user when component loads
    const shouldRun = window.confirm(
      "Would you like to automatically run the demo?"
    );
    if (shouldRun) {
      handleDemo();
    } else {
      console.log("Demo canceled by user.");
    }
  }, []); // runs once on mount */}
  if (loading || ent.__loading) 
    return <div className="ultimateCenter" style={{marginTop: "20%"}}>
      <l-dot-stream size="150" speed="1.5" color="white" />
    </div>;
  return (
    <>
      {fileUploadVisible && (
        <div className="container">
          <Card bg="dark" text="light" className="shadow-sm border-0">
            <Card.Body style={{ position: "relative" }}>
              {/* Stepper */}
              {/* <div className="stepper mb-3">
                <div className={`step ${step >= 1 ? "active" : ""}`}>
                  <div className="dot" />
                  <span>Start</span>
                </div>
                <div className="rail" />
                <div className={`step ${step >= 2 ? "active" : ""}`}>
                  <div className="dot" />
                  <span>Custom</span>
                </div>
              </div> */}

              {step === 1 && (
                <div className="hero-container">
                <div className="hero">
                  <h2 className="mb-2">Welcome to Grade Academy</h2>
                  <p className="lead text-white mb-4">
                    Dashboards that turn grades into timely, actionable insights.
                  </p>
                  <p className="text-white mb-4" style={{ maxWidth: 720 }}>
                    This tool helps you analyze historical grades and predict future student success.
                  </p>
                  <p className="text-white mb-4" style={{ maxWidth: 720 }}>
                    Explore with demo data or <a  style={{ textDecoration: "underline" }} href="/membership-levels">upgrade</a> and upload your own.
                  </p>

                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-success"
                      size="md"
                      className=" text-white"
                      onClick={handleDemo}
                      disabled={loadingDemo}
                    >{"Start Here to Run Demo"} <span className="chev">›</span>
                    </Button>
                    <Button
                      variant="outline-primary"
                      className=" text-white"
                      size="md"
                      onClick={() => setStep(2)}
                    >
                      Process My Data <span className="chev">›</span>
                    </Button>


                  </div>

                  {/* Data disclaimer */}
                  <div
                    style={{
                      fontSize: "0.85rem",
                      marginTop: "12px",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <strong>Note:</strong> We do not store or transmit your data.  
                    See our{" "}
                    <a
                      href="/privacy.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "underline" }}
                    >
                      Privacy Policy
                    </a>
                    ,{" "}
                    <a
                      href="/data-handling.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "underline" }}
                    >
                      Data Handling Policy
                    </a>
                    ,{" "}
                    <a
                      href="/ferpa.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "underline" }}
                    >
                      FERPA
                    </a>
                    ,{" "}
                    <a
                      href="/coppa.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "underline" }}
                    >
                      COPPA
                    </a>{" "}
                    compliance for details.
                  </div>
                </div>
                </div>
              )}

              {step === 2 && (
                <>
                  <DevBypassFeature
                    name="customDataUpload"
                    minPlan="monthly"
                    fallback={
                      <UpgradeBanner
                        setStep={setStep}
                        needed="monthly"
                        message="Analytics for your custom data is available on Monthly, and Yearly plans."
                      />
                    }
                  >
                    {/* your upload UI */}
                    <h3 className="mb-3">Upload your data</h3>
                    <p className="text-white" style={{ maxWidth: 720 }}>
                      You can upload historic grade data (required) and optional enrollment and filter files.
                      Files are processed in the browser; no data is sent to a server.
                    </p>

                    <div>
                      <Dropzone
                        label="Grade Data"
                        required
                        file={gradeFile}
                        onFile={setGradeFile}
                        accept=".csv,.xlsx,.xls,.json"
                        helper="CSV/XLSX of historical grades."
                        sampleHref="https://grade.academy/wp-content/uploads/2025/01/350_grades.csv"
                      />
                      <Dropzone
                        label="Enrollment Data (Optional)"
                        file={enrollFile}
                        onFile={setEnrollFile}
                        accept=".csv,.xlsx,.xls,.json"
                        helper="Future enrollment data to forecast success."
                        sampleHref="https://grade.academy/wp-content/uploads/2025/01/350_enrollment.csv"
                      />
                      {/* <Dropzone
                        label="Filter IDs (Optional)"
                        file={filterFile}
                        onFile={setFilterFile}
                        accept=".csv,.txt"
                        helper="List of student IDs (one per line or comma-separated)."
                      /> */}
                    </div>

                    <div className="mt-3 d-none">
                      <Form.Group className="mb-2">
                        <Form.Control type="file" ref={gradeRef} accept=".csv,.xlsx,.xls,.json" />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Control type="file" ref={enrollRef} accept=".csv,.xlsx,.xls,.json" />
                      </Form.Group>
                      {/* <Form.Group className="mb-2">
                        <Form.Control type="file" ref={filterRef} accept=".csv,.txt" />
                      </Form.Group> */}
                    </div>

                    <div className="d-flex gap-2 mt-3">
                      <Button variant="light" className="" onClick={() => setStep(1)}>
                        Back
                      </Button>
                      <Button
                        variant="outline-success"
                        className="" 
                        onClick={handleProcessFiles}
                        disabled={!gradeFile && !(gradeRef.current?.files?.length)}
                      >
                        Continue to Dashboard
                      </Button>
                    </div>
                  </DevBypassFeature>
                </>
              )}

            </Card.Body>
          </Card>
        </div>
      )}

      {/* Handoff to the main app once data is ready */}
      <GradeAcademyProcess
        demoMode={demoMode}
        key={uuidv4()}
        setIsLoading={props.setIsLoading}
        profileModalOpen={props.profileModalOpen}
        openProfileModal={props.openProfileModal}
        closeProfileModal={props.closeProfileModal}
        academyData={props.academyData}
        handleModalOpenChange={props.handleModalOpenChange}
        studentProfile={props.studentProfile}
        isModalOpen={props.isModalOpen}
        setIsModalOpen={props.setIsModalOpen}
        selectedStudentIdRef={props.selectedStudentIdRef}
        fileUploadVisible={fileUploadVisible}
        setFileUploadVisible={setFileUploadVisible}
        dataSectionToRender={props.dataSectionToRender}
        filterData={filterData}
        enrollment={enrollmentData}
        data={lineData}
      />
    </>
  );
};

export default function WrappedFileUpload(props) {
  console.log("WrappedFileUpload props:", props);
  return (
    <EntitlementsProvider>
      <FileUpload {...props} />
    </EntitlementsProvider>
  );
}